import { NgClass } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { DataView } from 'primeng/dataview';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { SelectButton } from 'primeng/selectbutton';
import Media from '../../../../types/media/Media';
import { IsMaintainerDirective } from '../../directives/is-maintainer.directive';
import { MediaService } from '../../services/media.service';
import { ReplaceFileDialogComponent } from '../shared/media/replace-file-dialog/replace-file-dialog.component';
import { UpdateMediaDialogComponent } from '../shared/media/update-media-dialog/update-media-dialog.component';
import { UploadMediaComponent } from '../shared/media/upload-media/upload-media.component';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';

@Component({
    selector: 'app-media-upload',
    templateUrl: './media.component.html',
    styleUrls: [ './media.component.scss' ],
    imports: [
        UploadMediaComponent,
        DataView,
        Select,
        FormsModule,
        SelectButton,
        NgClass,
        Button,
        PageHeaderComponent,
        IftaLabelModule,
        IconField,
        InputIcon,
        InputText,
        UpdateMediaDialogComponent,
        ReplaceFileDialogComponent,
        IsMaintainerDirective
    ],
    providers: [ MessageService ]
})
export class MediaComponent implements OnInit {
    media: Media[] = [];
    currentPage: number = 0;
    totalPages: number = 1;
    totalElements: number = 0;
    public search: string = '';

    @ViewChild(UpdateMediaDialogComponent) updateMediaDialog: UpdateMediaDialogComponent | undefined;
    public editDialogVisible = false;
    public replaceDialogVisible = false;
    public selectedMedia: Media = {
        id: 0,
        filename: '',
        description: '',
        content: undefined,
        createdAt: '',
        updatedAt: '',
        createdBy: '',
        updatedBy: '',
        version: 0
    };

    public sortKey: string = 'filename'; // Default sorting by filename
    public sortOrder: number = 1;

    public layout: 'list' | 'grid' = 'grid'; // Default layout
    public layoutOptions: string[] = [
        'grid',
        'list'
    ];
    public sortOptions = [
        { label: 'Filename (A-Z)', value: 'filename' },
        { label: 'Last Updated (Newest First)', value: 'updatedAt' },
    ];

    constructor(
        private mediaService: MediaService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService
    ) {
    }

    /**
     * Initializes the component.
     */
    public ngOnInit(): void {
        this.loadMedia();
    }

    /**
     * Reloads the media data with a delay.
     */
    public reloadMediaWithDelay(): void {
        setTimeout(() => {
            this.loadMedia();
        }, 2000);
    }

    /**
     * Loads media data from the server with pagination and sorting.
     */
    protected loadMedia() {
        this.mediaService.getPaginatedMedia(
            this.currentPage, 8, this.search, this.sortKey, this.sortOrder === 1 ? 'asc' : 'desc')
            .then((response) => {
                this.media = response.content;
                this.totalPages = response.page.totalPages;
                this.totalElements = response.page.totalElements;
            }).catch(() => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error Loading Media',
                detail: 'An unexpected error occurred while loading the media.',
                life: 3000
            });
        });
    }

    /**
     * Handles sorting changes.
     */
    public onSortChange(event: any):
        void {
        this.sortKey = event.value;
        this.sortOrder = this.sortKey === 'filename' ? 1 : -1; // Default: Ascending for filename, Descending for
                                                               // updatedAt
        this.loadMedia(); // Reload media with updated sorting
    }

    /**
     * Handles page changes.
     */
    public onPageChange(event: any): void {
        this.currentPage = event.first / event.rows;
        this.loadMedia();
    }

    /**
     * Handles search input changes.
     */
    public onSearchInputChange() {
        this.currentPage = 0;
        this.loadMedia();
    }

    /**
     * Opens the dialog to edit a media item.
     *
     * @param media The media item to edit.
     */
    public openEditDialog(media: Media) {
        this.selectedMedia = media;
        this.editDialogVisible = true;
    }

    /**
     * Opens the dialog to replace a media item.
     *
     * @param media The media item to replace.
     */
    public openReplaceDialog(media: Media) {
        this.selectedMedia = media;
        this.replaceDialogVisible = true;
    }

    /**
     * Opens a confirmation dialog to delete a media item.
     */
    public openDeleteDialog(media: Media) {
        this.confirmationService.confirm({
            message: `Are you sure you want to delete ${ media.filename }?`,
            header: 'Delete Media',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.mediaService.deleteMedia(media.id).then(() => {
                    this.loadMedia();
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Media Deleted',
                        detail: `${ media.filename } has been deleted.`,
                        life: 3000
                    });
                }).catch(() => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error Deleting Media',
                        detail: `An unexpected error occurred while deleting ${ media.filename }.`,
                        life: 3000
                    });
                });
            }
        });
    }
}
